import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, ShieldCheck, Zap, Star } from "lucide-react";
import PayPalCheckout from "../components/PayPalCheckout";
import PayPalProvider from "../components/PayPalProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { starter: ShieldCheck, pro: Zap, enterprise: Star };

export default function Deposit() {
  const [pkgs, setPkgs] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => { axios.get(`${API}/deposit-packages`).then((r) => setPkgs(r.data.packages || [])); }, []);

  async function buy(pkgId) {
    if (!email || !name) { toast.error("Provide name & email before checkout."); return; }
    setLoadingId(pkgId);
    try {
      const res = await axios.post(`${API}/checkout/session`, { package_id: pkgId, origin_url: window.location.origin, customer_email: email, customer_name: name });
      if (res.data.url) window.location.href = res.data.url;
    } catch { toast.error("Checkout unavailable — please try again."); setLoadingId(null); }
  }

  return (
    <PayPalProvider mode="capture">
    <div data-testid="deposit-page" style={{ background: "var(--ivory)" }}>
      <section className="pt-16 pb-10">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--muted)" }}>Chapter Five · Direct Booking</div>
          <h1 className="font-display mt-4" style={{ fontSize: "clamp(3rem, 8vw, 6rem)", lineHeight: 0.9, letterSpacing: "-0.04em" }}>
            Book &amp; <span className="font-display-italic" style={{ color: "var(--brass)" }}>pay.</span>
          </h1>
          <p className="mt-8 font-body text-lg max-w-2xl" style={{ color: "var(--navy-soft)" }}>Skip the phone tag. Lock in a slot with a refundable deposit — applied to your first invoice.</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="thin-border p-6 md:p-8 mb-10 grid sm:grid-cols-2 gap-4" style={{ background: "var(--ivory-2)" }}>
            <input data-testid="deposit-name" className="field-boxed" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
            <input data-testid="deposit-email" className="field-boxed" placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {pkgs.map((p) => {
              const Icon = ICONS[p.id] || ShieldCheck;
              const featured = p.id === "pro";
              return (
                <div key={p.id} className="thin-border p-8 flex flex-col" style={{ background: featured ? "var(--brass)" : "var(--ivory)", color: featured ? "var(--navy)" : "var(--navy)", borderColor: featured ? "var(--brass)" : "var(--line)" }} data-testid={`deposit-card-${p.id}`}>
                  {featured && <div className="font-mono text-[9px] uppercase tracking-[0.24em] mb-4">Recommended</div>}
                  <Icon size={26} style={{ color: featured ? "var(--navy)" : "var(--brass)" }} />
                  <h3 className="font-display text-3xl mt-4" style={{ letterSpacing: "-0.03em" }}>{p.name}</h3>
                  <p className="text-sm mt-2" style={{ color: featured ? "rgba(10,17,40,0.7)" : "var(--muted)" }}>{p.description}</p>
                  <div className="mt-6 font-display text-6xl" style={{ letterSpacing: "-0.03em" }}>${p.amount.toFixed(0)}</div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.24em] mt-1" style={{ color: featured ? "rgba(10,17,40,0.6)" : "var(--muted)" }}>USD · Refundable</div>
                  <button
                    data-testid={`buy-${p.id}`}
                    disabled={loadingId === p.id}
                    onClick={() => buy(p.id)}
                    className="mt-8 w-full justify-center"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "0.6rem",
                      background: featured ? "var(--navy)" : "var(--navy)", color: "var(--ivory)",
                      fontFamily: "'Space Mono', monospace", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.24em",
                      padding: "1rem 1.5rem", border: "1px solid var(--line-2)", cursor: "pointer",
                    }}
                  >
                    {loadingId === p.id ? "Redirecting…" : "Pay & Book"} <ArrowUpRight size={14} />
                  </button>

                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex-1" style={{ height: 1, background: featured ? "rgba(10,17,40,0.15)" : "var(--line)" }} />
                    <span className="font-mono text-[9px] uppercase tracking-[0.28em]" style={{ color: featured ? "rgba(10,17,40,0.55)" : "var(--muted)" }}>or</span>
                    <div className="flex-1" style={{ height: 1, background: featured ? "rgba(10,17,40,0.15)" : "var(--line)" }} />
                  </div>

                  <div className="mt-3">
                    <PayPalCheckout
                      testId={`paypal-deposit-${p.id}`}
                      orderPayload={{ package_id: p.id, customer_email: email, customer_name: name }}
                      validate={() => Boolean(name && email)}
                      validateMsg="Provide name & email before paying with PayPal."
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.24em] text-center" style={{ color: "var(--muted)" }}>Payments secured by Stripe or PayPal · Refundable</p>

          <div className="mt-10 thin-border p-6 text-center" style={{ background: "var(--ivory-2)" }}>
            <div className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--brass)" }}>Prefer monthly?</div>
            <p className="mt-2 font-body text-lg">
              Locked-in monthly retainers start at $1,200/mo — bi-weekly to weekly cadence with photo debriefs.
            </p>
            <a href="/retainer" data-testid="deposit-see-retainer" className="btn-ghost mt-4 inline-flex">View Retainer Tiers <ArrowUpRight size={14} /></a>
          </div>
        </div>
      </section>
    </div>
    </PayPalProvider>
  );
}
